import { type IFeatureFlags } from '@audacy-clients/core/atoms/config/settings';
import clamp from 'lodash/clamp';
import { interpret, type SingleOrArray, type Event, type StateValueMap } from 'xstate';

import AudacyLogger, { LoggerTopic } from '../AudacyLogger';
import { type AdvertisingIdFunction, type IDeviceInfoProvider } from '../Config';
import { type Platform, type Preference } from '../Constants';
import {
  type ICredentialsProvider,
  type ILocationProvider,
  type IMarketingDataProvider,
} from '../Container';
import { type PlaybacksHashMap } from '../dataServices/DataServices';
import { enableObjectTracing, type ILogger } from '../logger';
import { type UnauthenticatedServices } from '../personalizationServices';
import { type IPlayerSettingsProvider } from '../personalizationServices/types';
import { audioServicesMachine, ACTIVE_STREAMER_ID } from './audioServicesMachine';
import { type IPlayer, type TPlaybackRate } from './players/types';
import { type IStreamerMachineContext } from './streamers/types';
import { timeFromMs } from './streamers/utils';
import {
  ECollectionPlaybackMode,
  ESleepTimerState,
  type IAnalyticsProvider,
  type IAudioServicesMachineContext,
  type IAudioServicesState,
  type IAudioServicesStateListenerParams,
  type IDataProvider,
  type IPersonalizationProvider,
  type TAudioServicesListener,
  type TAudioServicesMachineEvent,
  type TAudioServicesSignalWireListener,
  type TPlayableObject,
} from './types';
import { bindPlayer, extractRootState, startInspector } from './utils';

startInspector();

// TODO [CCS-1384] ANALYTICS ON ALL STREAMERS!
type IAudioServicesConfig = {
  analyticsProvider: IAnalyticsProvider;
  credentialsProvider?: ICredentialsProvider;
  dataProvider: IDataProvider;
  domParser?: object;
  enableAmperWave?: boolean;
  enableAmperWaveRewind?: boolean;
  locationProvider?: ILocationProvider;
  logger: ILogger;
  marketingDataProvider?: IMarketingDataProvider;
  deviceInfoProvider?: IDeviceInfoProvider;
  personalizationProvider: IPersonalizationProvider;
  platform: Platform;
  player: IPlayer;
  playerSettingsProvider: IPlayerSettingsProvider;
  rewindAds?: boolean;
  unauthenticatedProvider?: UnauthenticatedServices;
  webcastMetricsStr: string;
  featureFlags?: IFeatureFlags;
  useMp3RewindStreamer?: boolean;
  eventListener?: TAudioServicesListener;
  getAdvertisingId?: AdvertisingIdFunction;
};

export default class AudioServices {
  logger?: ILogger;
  previousParentState = '';
  previousChildState = '';
  private eventListener?: TAudioServicesListener;
  private send: (event: SingleOrArray<Event<TAudioServicesMachineEvent>>) => unknown;
  private getRootMachineContext: () => IAudioServicesMachineContext;
  private getStreamerMachineContext: () => IStreamerMachineContext<TPlayableObject>;

  constructor(config: IAudioServicesConfig) {
    this.eventListener = config.eventListener;
    this.logger = config.logger;

    const machineContext: IAudioServicesMachineContext = {
      activeCollection: [],
      activePlayableObject: undefined,
      analyticsProvider: config.analyticsProvider,
      autoplay: false,
      collectionPlaybackMode: ECollectionPlaybackMode.SingleItem,
      credentialsProvider: config.credentialsProvider,
      dataProvider: config.dataProvider,
      isMuted: false,
      locationProvider: config.locationProvider,
      logger: config.logger,
      marketingDataProvider: config.marketingDataProvider,
      personalizationProvider: config.personalizationProvider,
      platform: config.platform,
      playbacks: {},
      player: config.player,
      playerSettingsProvider: config.playerSettingsProvider,
      deviceInfoProvider: config.deviceInfoProvider,
      playLive: false,
      rate: 1,
      skipsCount: 0,
      sleepTimerState: ESleepTimerState.Idle,
      unauthenticatedProvider: config.unauthenticatedProvider,
      volume: 1,
      playbackTimer: {
        accumulator: timeFromMs(0),
        elapsed: timeFromMs(0),
      },
      featureFlags: config.featureFlags,
      useMp3RewindStreamer: config.useMp3RewindStreamer,
    };

    const machine = audioServicesMachine.withContext(machineContext);

    const service = interpret(machine, { devTools: true });

    bindPlayer(config.player, service.send, config.logger);
    this.send = service.send;
    this.getRootMachineContext = () => service.getSnapshot().context;
    this.getStreamerMachineContext = () =>
      service.getSnapshot().children[ACTIVE_STREAMER_ID]?.getSnapshot().context;

    service.onTransition((state) => {
      // XState has a bug where it triggers onTransition on the parent machine just before the
      // child machine actually transitions. This causes a stale state in onTransition.
      // Adding setTimeout allows us to wait for that child machine to transition and fix the stale state.
      // Here's a link to the bugged code. It schedules the child transition for later then triggers onTransition
      // https://github.com/statelyai/xstate/blob/3b4b1305af6fa5db288ffdb3b4918a05048353e4/packages/core/src/interpreter.ts#L752-L758
      setTimeout(() => {
        const streamerSnapshot = state.children[ACTIVE_STREAMER_ID]?.getSnapshot();

        const {
          activePlayableObject,
          playbacks,
          sleepTimerState,
          rate,
          volume,
          isMuted,
          activeCollection,
          collectionPlaybackMode,
        } = state.context;

        const audioServicesState: IAudioServicesState = {
          value: state.value as StateValueMap,
          context: {
            activePlayableObject,
            playbacks,
            sleepTimerState,
            rate,
            volume,
            isMuted,
            activeCollection,
            collectionPlaybackMode,
          },
        };

        // streamerState.value is the child machine state e.g. LOADING
        // we also use streamerSnapshot.context e.g. requestedOffset
        const streamerState = streamerSnapshot
          ? {
              ...streamerSnapshot,
              value: extractRootState(streamerSnapshot.value),
            }
          : undefined;

        this.logger?.trace(
          `AudioServices machine state: ${JSON.stringify(Object.values(state.value)[0])}, ${
            streamerState
              ? `Child streamer machine state: ${JSON.stringify(
                  streamerState?.historyValue?.current || streamerState.value,
                  null,
                  2,
                )}`
              : ''
          }`,
        );

        if (Object.values(state.value)[0] != this.previousParentState) {
          this.previousParentState = Object.values(state.value)[0] as string;
          AudacyLogger.info(
            `[${
              LoggerTopic.Streaming
            }] AudioServices: Parent streamer machine state: ${JSON.stringify(
              Object.values(state.value)[0],
            )}`,
          );
        }
        const childState = JSON.stringify(
          streamerState ? streamerState?.historyValue?.current || streamerState.value : undefined,
        );
        if (childState != this.previousChildState) {
          this.previousChildState = childState;
          AudacyLogger.info(
            `[${LoggerTopic.Streaming}] AudioServices: Child streamer machine state: ${childState}`,
          );
        }

        const payload: IAudioServicesStateListenerParams = {
          audioServicesState,
          streamerState,
        };

        this.eventListener?.(payload);
      }, 0);
    });

    service.start();
  }

  setMicMuted(isMuted: boolean) {
    this.send({ type: 'SET_MIC_MUTED', isMuted });
    // TODO: send SET_MIC_MUTED to future logging service
  }

  setSleepTimerDuration(durationInMin: number) {
    const durationInMsec = durationInMin * 60 * 1000;
    const timeToSleep = Date.now() + durationInMsec;
    this.send({ type: 'SET_SLEEP_TIMER', sleepTimer: timeToSleep });
    // TODO: send SET_SLEEP_TIMER to future logging service
  }

  setSleepTimerForCurrentEpisode() {
    this.send({
      type: 'SET_SLEEP_TIMER',
      sleepTimer: ESleepTimerState.EndOfEpisode,
    });
    // TODO: send SET_SLEEP_TIMER to future logging service
  }

  cancelSleepTimer() {
    this.send({ type: 'CANCEL_SLEEP_TIMER' });
    // TODO: send CANCEL_SLEEP_TIMER to future logging service
  }

  setCollectionPlaybackMode = (mode: ECollectionPlaybackMode) =>
    this.send({ type: 'SET_COLLECTION_PLAYBACK_MODE', mode });

  getCollectionPlaybackMode = () => this.getRootMachineContext().collectionPlaybackMode;

  setEventListener = (eventListener?: TAudioServicesListener) =>
    (this.eventListener = eventListener);

  setSignalWireEventListener = (signalWireEventListener: TAudioServicesSignalWireListener) => {
    this.send({
      type: 'SET_SIGNAL_WIRE_EVENT_LISTENER',
      signalWireEventListener,
    });
    // TODO: send SET_SIGNAL_WIRE_EVENT_LISTENER to future logging service
  };

  setVolume(volume: number) {
    this.send({ type: 'SET_MUTED', isMuted: volume === 0 });
    this.send({ type: 'SET_VOLUME', volume: clamp(volume, 0, 1) });
  }

  setMuted(isMuted: boolean) {
    this.send({ type: 'SET_MUTED', isMuted });
    // TODO: send SET_MUTED to future logging service
  }

  setPlaybackRate(rate: TPlaybackRate) {
    this.send({ type: 'SET_PLAYBACK_RATE', rate });
    // TODO: send SET_PLAYBACK_RATE to future logging service
  }

  getPlayer() {
    return this.getRootMachineContext().player;
  }

  getVolume() {
    return this.getRootMachineContext().volume;
  }

  getMuted() {
    return this.getRootMachineContext().isMuted;
  }

  getIsLive() {
    return this.getStreamerMachineContext()?.metadata?.isLive ?? false;
  }

  seekToTime(timeInSecs: number) {
    this.send({ type: 'SEEK_TIME', time: timeInSecs });
    // TODO: send SEEK_TIME to future logging service
  }

  seekToProgress(progress: number) {
    this.seekToTime(progress * (this.getStreamerMachineContext()?.metadata?.duration ?? 0));
    // TODO: send seekToProgress to future logging service
  }

  scrubbed() {
    this.send({ type: 'SCRUB' });
  }

  next(shouldSkipCompletedItems = false) {
    this.send({ type: 'NEXT', shouldSkipCompletedItems });
    // TODO: send NEXT to future logging service
  }

  previous(shouldSkipCompletedItems = false) {
    this.send({ type: 'PREVIOUS', shouldSkipCompletedItems });
    // TODO: send PREVIOUS to future logging service
  }

  skip() {
    this.send({ type: 'SKIP' });
    // TODO: send SKIP to future logging service
  }

  skipByTime(timeInSecs: number, onCallback?: (value: number) => void) {
    const elapsed = this.getStreamerMachineContext()?.elapsed ?? 0;
    const seekTime = elapsed + timeInSecs;

    this.seekToTime(seekTime);
    // TODO: send skipByTime to future logging service
    onCallback?.(seekTime);
  }

  sendPreference(preference: Preference) {
    this.send({ type: 'SEND_PREFERENCE', preference });
  }

  goToLive() {
    this.send({ type: 'GO_TO_LIVE' });
    // TODO: send GO_TO_LIVE to future logging service
  }

  play(dataObj?: TPlayableObject, autoplay = true, offset?: number, playLive = false) {
    if (dataObj) {
      this.send({ type: 'STOP' });
      this.send({
        type: 'LOAD_OBJECT',
        data: dataObj,
        autoplay,
        offset,
        playLive,
      });
    } else {
      this.send({ type: 'PLAY', autoplay });
    }
    // TODO: send PLAY to future logging service
  }

  updatePlaybacks(playbacks: PlaybacksHashMap) {
    this.send({
      type: 'UPDATE_PLAYBACKS',
      data: playbacks,
    });
    // TODO: send UPDATE_PLAYBACKS to future logging service
  }
  updateCollection(collection: Array<TPlayableObject>) {
    this.send({
      type: 'UPDATE_COLLECTION',
      data: collection,
    });
    // TODO: send UPDATE_COLLECTION to future logging service
  }

  // TODO [CCS-1389] Do we need this convenience method? I think it should be eliminated
  playCollection(collection: Array<TPlayableObject>, dataObj: TPlayableObject) {
    this.updateCollection(collection);
    this.play(dataObj);
  }

  pause() {
    this.send({ type: 'PAUSE' });
    // TODO: send PAUSE to future logging service
  }

  stop() {
    this.send({ type: 'STOP' });
    // TODO: send STOP to future logging service
  }

  incrementRate() {
    this.send({ type: 'INCREMENT_PLAYBACK_RATE' });
    // TODO: send INCREMENT_PLAYBACK_RATE to future logging service
  }
}

enableObjectTracing(AudioServices);
